import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { TrustProfile } from '../api/contract'
import { relativeTime } from '../lib/time'
import { TourPanel } from './TourPanel'
import './LowerGrid.css'
import { InfoTip } from './InfoTip'

function CheckIcon({ complete }: { complete: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="10" cy="10" r="7.5" />
      {complete && <path d="m6.6 10.2 2.4 2.4 4.5-5" />}
    </svg>
  )
}

function TeamActivity() {
  const { data, isPending } = useQuery({
    queryKey: ['team-activity'],
    queryFn: () => api.getTeamActivity(),
  })

  return (
    <div className="card lo-a" style={{ padding: '18px 0 6px' }}>
      <h2 className="h2" style={{ padding: '0 16px 8px' }}>Team Activity <InfoTip tip="teamActivity" /></h2>
      {isPending && <div className="ac"><span className="ac-t">Loading activity…</span></div>}
      {data?.items.map((ev) => (
        <div className="ac" key={ev.id}>
          <span className="ac-i">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <circle cx="10" cy="7" r="2.8" />
              <path d="M4 16.5c0-3 2.7-5.2 6-5.2s6 2.2 6 5.2" />
            </svg>
          </span>
          <span className="ac-t"><b>{ev.actor_name}</b> {ev.summary}</span>
          <span className="ac-w">{relativeTime(ev.occurred_at)}</span>
        </div>
      ))}
      {data && data.items.length === 0 && (
        <div className="ac"><span className="ac-t">No team activity yet.</span></div>
      )}
      <div style={{ padding: '10px 16px', textAlign: 'right' }}>
        <Link className="lnk" to="/team-activity">View All Activity &nbsp;→</Link>
      </div>
    </div>
  )
}

function InviteTeam() {
  // Copying genuinely works — it is pure client-side. The link it copies points
  // at a real route; what does not exist yet is invite-token generation.
  const [copied, setCopied] = useState(false)
  const inviteUrl = `${window.location.origin}/team-permissions`

  return (
    <div className="card inv lo-b">
      <h3 className="inv-h">Bring Your Hiring Team Into the Decision</h3>
      <p className="inv-p">
        Invite your CEO, CHRO, COO, CFO, HR leaders, or hiring managers to collaborate on executive hiring
      </p>
      <div className="inv-b">
        <Link className="btn btn-teal" to="/team-permissions">Invite Team Member</Link>
        <button
          className="btn" type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(inviteUrl)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
          }}
        >
          {copied ? 'Link copied ✓' : 'Copy Invite Link'}
        </button>
      </div>
      <p className="inv-p">
        Invitee gets full access to collaborate on pipelines, candidates, and decision documentation
      </p>
    </div>
  )
}

/**
 * The badge must follow `rating`, not always render green.
 *
 * `rating` and `rating_label` are separate fields in the contract precisely so
 * the colour is driven by the enum rather than parsed out of the label. The
 * previous version hardcoded `b-g`, so a `needs_attention` company looked
 * identical to a healthy one — a status indicator that fails reassuringly.
 */
const TRUST_BADGE: Record<TrustProfile['rating'], string> = {
  strong: 'bdg b-g',
  building: 'bdg b-a',
  needs_attention: 'bdg b-r',
}

function TrustProfileCard() {
  const { data, isPending } = useQuery({
    queryKey: ['trust-profile'],
    queryFn: () => api.getTrustProfile(),
  })

  return (
    <div className="card lo-c" style={{ padding: '18px 20px' }}>
      <div className="tp-h">
        <h3 className="h2" style={{ fontSize: '14.5px' }}>Company Trust Profile</h3>
        {data && (
          <span className={TRUST_BADGE[data.rating]} style={{ marginLeft: 'auto' }}>
            {data.rating_label}
          </span>
        )}
      </div>
      {isPending && <div className="tp-k">Loading…</div>}
      {data && (
        <>
          <div className="tp-k">{data.headline}</div>
          <div className="cks">
            {data.items.map((item) => (
              <div className={`ck${item.complete ? '' : ' todo'}`} key={item.key}>
                <CheckIcon complete={item.complete} />
                {item.label}
                {item.meta && <span className="ck-m">{item.meta}</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function SummaryPreview() {
  const { data, isPending } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => api.getCompanyProfile(),
  })

  return (
    <div className="card lo-d" style={{ padding: '18px 20px' }}>
      <div className="pv-h">
        <h3 className="h2" style={{ fontSize: '14.5px' }}>Candidate-Facing Summary Preview</h3>
        {/* goes to the area shared with candidates — locked 2026-07-18 */}
        <Link className="pv-edit" to={data?.edit_url ?? '/company-profile'} aria-label="Edit your company logo and candidate-facing profile">
          <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M13.5 3.5 16.5 6.5M14.8 2.2a1.9 1.9 0 0 1 2.7 2.7L7.2 15.2 3 17l1.8-4.2z" />
          </svg>
          Edit
        </Link>
      </div>

      <div className="pv" role="img" aria-label={data ? `${data.name} logo` : 'Company logo'}>
        {data?.logo_url ? (
          <img src={data.logo_url} alt="" />
        ) : (
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <path d="M3 21h18M5 21V6l7-3 7 3v15M9 10h2M13 10h2M9 14h2M13 14h2M9 18h2M13 18h2" />
          </svg>
        )}
      </div>

      {isPending && <p className="pv-p">Loading company profile…</p>}
      {data && (
        <>
          <p className="pv-p">{data.summary}</p>
          <div className="fx">
            {data.stage && <div className="f1"><span>Stage</span><span>{data.stage}</span></div>}
            {data.headquarters && <div className="f1"><span>Headquarters</span><span>{data.headquarters}</span></div>}
            {data.employee_range && <div className="f1"><span>Employees</span><span>{data.employee_range}</span></div>}
          </div>
        </>
      )}
    </div>
  )
}

export function LowerGrid() {
  return (
    <section className="sec">
      <div className="lower">
        <TeamActivity />
        <InviteTeam />
        <TrustProfileCard />
        <SummaryPreview />
        {/* must stay inside .lower — docking places it in the right rail */}
        <TourPanel />
      </div>
    </section>
  )
}
